import { useEffect, useMemo, useState } from "react";

const DEFAULT_PAGE_SIZE = 25;

const normalizeValue = (value) => (value ?? "").toString().trim().toLowerCase();

const compareValues = (left, right) => {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;

  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return normalizeValue(left).localeCompare(normalizeValue(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const resolveSortValue = (item, sortOption) => {
  if (!sortOption) return item;
  if (typeof sortOption.getSortValue === "function") return sortOption.getSortValue(item);
  if (typeof sortOption.getValue === "function") return sortOption.getValue(item);
  return item?.[sortOption.key];
};

export const filterItems = (items, searchText, matcher) => {
  const normalizedSearch = normalizeValue(searchText);
  if (!normalizedSearch) return items;
  if (typeof matcher !== "function") return items;

  return items.filter((item) => matcher(item, normalizedSearch));
};

export const sortItems = (items, sortKey, sortDir, sortOptions) => {
  const activeSort = sortOptions.find((option) => option.key === sortKey) ?? sortOptions[0];
  const direction = sortDir === "desc" ? -1 : 1;

  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftValue = resolveSortValue(left.item, activeSort);
      const rightValue = resolveSortValue(right.item, activeSort);

      let comparison;

      if (typeof activeSort?.compare === "function") {
        comparison = activeSort.compare(left.item, right.item);
      } else {
        comparison = compareValues(leftValue, rightValue);
      }

      if (comparison !== 0) return comparison * direction;

      if (activeSort?.fallbackKey) {
        const fallbackComparison = compareValues(
          left.item?.[activeSort.fallbackKey],
          right.item?.[activeSort.fallbackKey]
        );

        if (fallbackComparison !== 0) return fallbackComparison * direction;
      }

      return left.index - right.index;
    })
    .map(({ item }) => item);
};

export const paginateItems = (items, currentPage, pageSize) => {
  const startIndex = (currentPage - 1) * pageSize;
  return items.slice(startIndex, startIndex + pageSize);
};

const usePaginatedList = ({
  items,
  sortOptions,
  initialSortKey,
  initialSortDir = "asc",
  initialPageSize = DEFAULT_PAGE_SIZE,
  initialSearchText = "",
  searchMatcher,
}) => {
  const [searchText, setSearchText] = useState(initialSearchText);
  const [sortKey, setSortKey] = useState(initialSortKey ?? sortOptions?.[0]?.key ?? "");
  const [sortDir, setSortDir] = useState(initialSortDir);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredItems = useMemo(
    () => filterItems(items, searchText, searchMatcher),
    [items, searchMatcher, searchText]
  );

  const sortedItems = useMemo(
    () => sortItems(filteredItems, sortKey, sortDir, sortOptions),
    [filteredItems, sortDir, sortKey, sortOptions]
  );

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, searchText, sortDir, sortKey]);

  useEffect(() => {
    setCurrentPage((previousPage) => Math.min(previousPage, totalPages));
  }, [totalPages]);

  const paginatedItems = useMemo(
    () => paginateItems(sortedItems, currentPage, pageSize),
    [currentPage, pageSize, sortedItems]
  );

  const setSort = (nextSortKey, nextSortDir = "asc") => {
    setSortKey(nextSortKey);
    setSortDir(nextSortDir);
  };

  const toggleSort = (nextSortKey) => {
    if (sortKey === nextSortKey) {
      setSortDir((previousDir) => (previousDir === "asc" ? "desc" : "asc"));
      return;
    }

    setSort(nextSortKey, "asc");
  };

  return {
    filteredItems,
    sortedItems,
    paginatedItems,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    searchText,
    sortKey,
    sortDir,
    setCurrentPage,
    setPageSize,
    setSearchText,
    setSort,
    setSortDir,
    setSortKey,
    toggleSort,
  };
};

export default usePaginatedList;
